/**
 * analyticsApi — RTK Query slice for the admin analytics endpoints.
 * Endpoints:
 *   GET /analytics/summary  → AnalyticsSummary
 *   GET /analytics/activity → AnalyticsActivity
 */

import { api } from './api';

// ---- Response types (typed to match analytics.service.ts) ----------------

export interface BreakdownRow {
  value: number;
  label: string;
  count: number;
  pct: number;
}

export interface FunnelRow {
  step: string; // keyof OnboardingStep | 'COMPLETED'
  label: string;
  count: number;
  pct: number;
}

export interface EngagementMetrics {
  matchRatePct: number | null;
  avgMessagesPerChat7d: number;
  replyRatePct: number | null;
  d7RetentionPct: number | null;
}

export interface SummaryCards {
  activeToday: number;
  weeklyActive: number;
  newSignups7d: number;
  premiumConversionPct: number;
}

export interface AnalyticsSummary {
  generatedAt: string;
  cards: SummaryCards;
  membership: BreakdownRow[];
  membershipTotal: number;
  accountStatus: BreakdownRow[];
  onboardingFunnel: FunnelRow[];
  engagement: EngagementMetrics;
}

export type ActivityType =
  | 'match'
  | 'signup'
  | 'upgrade'
  | 'rsvp'
  | 'message_burst';

export interface ActivityItem {
  type: ActivityType;
  timestamp: string;
  title: string;
  meta?: Record<string, unknown>;
}

export interface AnalyticsActivity {
  generatedAt: string;
  items: ActivityItem[];
}

// ---- Query params ---------------------------------------------------------

export interface ActivityQueryParams {
  /** 1–100 */
  limit?: number;
  /** ISO timestamp — only items after this date */
  since?: string;
}

// ---- API slice ------------------------------------------------------------

export const analyticsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAnalyticsSummary: builder.query<AnalyticsSummary, void>({
      query: () => ({
        url: '/analytics/summary',
        method: 'GET',
      }),
    }),

    getAnalyticsActivity: builder.query<
      AnalyticsActivity,
      ActivityQueryParams | void
    >({
      query: (params) => ({
        url: '/analytics/activity',
        method: 'GET',
        params: params ?? {},
      }),
    }),
  }),
});

export const { useGetAnalyticsSummaryQuery, useGetAnalyticsActivityQuery } =
  analyticsApi;
